<template>
	<div>
		<Disclosure :title="$t('text.mainUserAccountTitle')" :error="sectionMessage.error" :message="sectionMessage.message" :lock="!userMayEditFields" data-cy="mainUserAccount" ref="SECTION_mainUserAccount">
			<div class="field left-border">
				<v-label>{{$t('text.personalName')}} <span class="error-text">({{$t('text.required')}})</span></v-label>
				<v-text-field variant="outlined" density="compact" autocomplete="off" required
					id="mainUserAccountName"
					v-model="mainUserAccount.name"
					:error-messages="mainUserAccountNameErrors"
					@update:modelValue="v$.mainUserAccount.name.$touch()"
					@blur="v$.mainUserAccount.name.$touch()"
				/>
			</div>
			<div class="field left-border">
				<v-label>{{$t('text.email')}} <span class="error-text">({{$t('text.required')}})</span></v-label>
				<v-text-field variant="outlined" density="compact" required autocomplete="off"
					:readonly="fieldLocks.email"
					id="username"
					v-model="mainUserAccount.email"
					:error-messages="mainUserAccountEmailErrors"
					@focus="unlockField('email')"
					@update:modelValue="v$.mainUserAccount.email.$touch()"
					@blur="checkEmailExists"
				/>
			</div>
			<div class="field left-border">
				<v-label>{{$t('text.password')}} <span class="error-text">({{$t('text.required')}})</span></v-label>
				<v-text-field required autocomplete="off" variant="outlined" density="compact"
					:readonly="fieldLocks.password"
					id="password"
					type="password"
					v-model="mainUserAccount.password"
					:error-messages="mainUserAccountPasswordErrors"
					:hint="passwordPolicyHint()"
					persistent-hint
					@focus="unlockField('password')"
					@update:modelValue="v$.mainUserAccount.password.$touch()"
					@blur="v$.mainUserAccount.password.$touch()"
				/>
			</div>
			<div class="field left-border">
				<v-label>{{$t('text.repeatPassword')}} <span class="error-text">({{$t('text.required')}})</span></v-label>
				<v-text-field type="password" variant="outlined" density="compact" required sameAs autocomplete="off"
					id="repeatPassword"
					v-model="mainUserAccount.repeatPassword"
					:error-messages="mainUserAccountRepeatPasswordErrors"
					@update:modelValue="v$.mainUserAccount.repeatPassword.$touch()"
					@blur="v$.mainUserAccount.repeatPassword.$touch()"
				/>
			</div>
		</Disclosure>
	</div>
</template>

<script>
	import Common from '@/mixins/Common.vue'
	import PasswordPolicy from '@/mixins/PasswordPolicy.vue'
	import Disclosure from '@/components/common/Disclosure.vue'
	import { useVuelidate } from "@vuelidate/core"
	import { required, minLength, maxLength, email, sameAs, helpers } from "@vuelidate/validators"
	import isEqual from 'lodash/isEqual'
	import { isValidPersonName } from '@/utils/input-validation.js'

	const personNameValidator = helpers.withMessage('', (value) => isValidPersonName(value))
	
	export default {
	  name: "UserAccount",
	  components: {
		Disclosure
	  },
	  mixins: [Common, PasswordPolicy],
	  emits: ['validity-changed'],
	 
	  props: {
		serviceProvider: {
		  type: Object,
		  required: true
		},
		updateModel: Boolean,
		runValidation: Boolean
	  },
	  setup() {
	  	return {
	  		v$: useVuelidate()
	  	}
	  },
	  data() {
		return {
		  model: {},
		  initData: {},
		  sectionMessage: {
			error: false,
			message: ''
		  },
		  //this is also called in the parent component via $refs
		  mainUserAccount: {
				name:'',
				email:'',
				password:'',
				repeatPassword:'',
		  },
		  fieldLocks: {
			email: true,
			password: true,
		  },
		  emailIsUnique: true,
		  lastValidityResult: null,
		}
	  },
	  validations() {
			return {
				mainUserAccount: {
					name: { required, minLength: minLength(4), maxLength: maxLength(128), personNameValidator },
					email: { required, email, minLength: minLength(4), maxLength: maxLength(256) },
					password: { required, passwordPolicy: this.passwordPolicyRule(this.mainUserAccount.email) },
					repeatPassword: { required, sameAsPassword: sameAs(this.mainUserAccount.password) }
				}
			}
		},
		computed: {
		mainUserAccountNameErrors() {
			const errors = []
			const { $dirty, required, minLength, maxLength, personNameValidator } = this.v$.mainUserAccount.name

			if (!$dirty) return errors

			if (required.$invalid) errors.push(this.$t('text.nameRequired'))
			if (minLength.$invalid) errors.push(this.$t('text.nameMinChars'))
			if (maxLength.$invalid) errors.push(this.$t('text.nameMaxChars'))
			if (personNameValidator.$invalid) errors.push(this.$t('text.fieldValPersonNameFormat'))

			return errors
		},
		mainUserAccountEmailErrors() {
			const errors = []
			const { $dirty, email, required, minLength, maxLength } = this.v$.mainUserAccount.email

			if (!$dirty) return errors

			if (email.$invalid) errors.push(this.$t('text.emailInvalid'))
			if (required.$invalid) errors.push(this.$t('text.emailRequired'))
			if (minLength.$invalid) errors.push(this.$t('text.emailMinChars'))
			if (maxLength.$invalid) errors.push(this.$t('text.emailMaxChars'))
			if (!this.emailIsUnique) errors.push(this.$t('text.registrationEmailExistsError'))

			return errors
		},
		mainUserAccountPasswordErrors() {
			const errors = []
			const { $dirty, required } = this.v$.mainUserAccount.password

			if (!$dirty) return errors

			if (required.$invalid) errors.push(this.$t('text.passwordRequired'))
			errors.push(...this.passwordPolicyMessages(this.mainUserAccount.password, this.mainUserAccount.email))

			return errors
		},
		mainUserAccountRepeatPasswordErrors() {
			const errors = []
			const { $dirty, sameAsPassword, required } = this.v$.mainUserAccount.repeatPassword

			if (!$dirty) return errors

			if (sameAsPassword.$invalid) errors.push(this.$t('text.repeatPasswordSame'))
			if (required.$invalid) errors.push(this.$t('text.repeatPasswordRequired'))

			return errors
		},
	  },
	  watch: {
		mainUserAccount: {
			deep: true,
			handler() {
				const isValid = this.validateAllFields()
				if (this.lastValidityResult !== isValid) {
					this.lastValidityResult = isValid
					this.$emit('validity-changed', isValid)
				}
			}
		},
		updateModel() {
			this.model = this.valueToModel(this.serviceProvider)
			this.setInitialUserAccount()
			this.setInitData()
		}
	  },

	  created() {
		this.model = this.valueToModel(this.serviceProvider)
		this.setInitialUserAccount()
		this.setInitData()
	  },
	  methods: {
		valueToModel(v) {
			return JSON.parse(JSON.stringify(v ?? {}))
		},
		setInitData() {
			const initModel = JSON.parse(JSON.stringify(this.model))
			this.initData = {
				mainUserAccount: {
					name:'',
					email:'',
					password:'',
					repeatPassword:''
				}
			}
		},
		sendData() {
			let data = {
				mainUserAccount: this.mainUserAccount
			}

			data.changed = !isEqual(data, this.initData)
			return data
		},
		setInitialUserAccount() {
			if (!this.model.fields.mainUserAccount) {
				this.mainUserAccount = {
					name:'',
					email:'',
					password:'',
					repeatPassword:''
				}
			}
		},
		validateAllFields() {
			let allFieldsAreValid = true
			this.resetSectionError(this.sectionMessage)	

			if (this.model.fields.mainUserAccount?.fields?.name) {
				if (!Object.keys(this.model.fields.mainUserAccount.fields).length || !this.objectValues(this.model.fields.mainUserAccount.fields)) {
					allFieldsAreValid = false
				}
			} else {
				if (this.mainUserAccount && Object.keys(this.mainUserAccount).length > 0) {
					if (!Object.keys(this.mainUserAccount).length || !this.objectValues(this.mainUserAccount)) {
						allFieldsAreValid = false
					} else {
						this.v$.mainUserAccount.name.$touch()
						this.v$.mainUserAccount.email.$touch()
						this.v$.mainUserAccount.password.$touch()
						this.v$.mainUserAccount.repeatPassword.$touch()

						if (this.mainUserAccountNameErrors.length ||
							this.mainUserAccountEmailErrors.length ||
							this.mainUserAccountPasswordErrors.length ||
							this.mainUserAccountRepeatPasswordErrors.length) {

							allFieldsAreValid = false
						}
					}
				}
			}
			if (!allFieldsAreValid && this.runValidation) {
				this.setSectionError(this.sectionMessage, this.$t('text.userAccountRequiredError'))
			}

			return allFieldsAreValid
		},
		async checkEmailExists() {
			const res = await this.$httpGet(`/check-email?email=${encodeURIComponent(this.mainUserAccount.email)}`)
			this.emailIsUnique = !res.emailExists
		},
		unlockField(field) {
			this.fieldLocks[field] = false
		},
	  }
	}
	</script>
	
	<style scoped>
	
	</style>
	
	
